import { MediaUploadUsage, type MediaUploadUsageValue } from '@/lib/enums/MediaUploadUsage'
import { getMediaUploadProfile } from '@/lib/media-upload-profiles'
import type { MediaFile, MediaPhotoAsset } from '@/lib/services/media/media.types'
import type {
  PhotoAsset,
  PhotoAssetWithMedia,
} from '@/lib/services/photo-assets/photo-assets.types'

const IMAGE_EXTENSION_REGEX = /\.(avif|gif|heic|heif|ico|jpe?g|png|svg|webp)$/i
const UNSUPPORTED_EDIT_EXTENSIONS = /\.(gif|ico|svg)$/i
const UNSUPPORTED_EDIT_MIME_TYPES = new Set([
  'image/gif',
  'image/x-icon',
  'image/vnd.microsoft.icon',
  'image/svg+xml',
])

/** A photo passed in its file's place carries its own source. */
type PhotoAssetSource = Partial<Pick<PhotoAsset, 'source_type' | 'source_photo_asset_id'>>

/** The file fields these editing/display helpers consume, including historical flat files.
 * Full serialized media/photo DTOs stay strict; these views do not invent missing focal points.
 */
type MediaEditingFile = Pick<
  MediaFile,
  | 'id'
  | 'url'
  | 'name'
  | 'path'
  | 'mime_type'
  | 'size'
  | 'width'
  | 'height'
  | 'upload_usage'
  | 'caption'
  | 'photo_asset'
  | 'photo_asset_id'
> & {
  title?: MediaFile['title'] | PhotoAsset['title']
}
type MediaEditingAsset = MediaEditingFile &
  Partial<
    Pick<
      PhotoAsset,
      | 'folder_id'
      | 'original_media_id'
      | 'latest_media_id'
      | 'source_photo_asset_id'
      | 'source_type'
    >
  > & {
    latest_media?: MediaEditingFile | null
    original_media?: MediaEditingFile | null
  }

/** How many chosen photos a field took, and how many it skipped. */
export interface AssetSelectionCounts {
  insertedCount: number
  skippedCount: number
}

const MIME_EXTENSION_MAP: Record<string, string> = {
  'image/avif': 'avif',
  'image/gif': 'gif',
  'image/x-icon': 'ico',
  'image/vnd.microsoft.icon': 'ico',
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/svg+xml': 'svg',
  'image/webp': 'webp',
}

function getMediaExtension(
  media: Pick<MediaFile, 'mime_type' | 'name' | 'path' | 'url'> | null | undefined
) {
  const mimeExtension = MIME_EXTENSION_MAP[String(media?.mime_type || '').toLowerCase()]
  if (mimeExtension) {
    return `.${mimeExtension}`
  }

  for (const candidate of [media?.name, media?.path, media?.url]) {
    const pathname = String(candidate || '').split(/[?#]/, 1)[0] ?? ''
    const extension = pathname.match(/\.[a-z0-9]+$/i)?.[0]

    if (extension) {
      return extension.toLowerCase()
    }
  }

  return null
}

export function isMediaCompatibleWithUploadUsage(
  media: (MediaEditingFile & Pick<PhotoAssetSource, 'source_type'>) | null | undefined,
  uploadUsage: MediaUploadUsageValue
): boolean {
  if (!media?.id || !media?.url) {
    return false
  }

  const profile = getMediaUploadProfile(uploadUsage)
  const extension = getMediaExtension(media)
  const allowedFileTypes = new Set(
    profile.restrictions.allowedFileTypes.map((fileType) => fileType.toLowerCase())
  )

  if (!extension || !allowedFileTypes.has(extension)) {
    return false
  }

  const size = Number(media.size)
  if (Number.isFinite(size) && size > profile.restrictions.maxFileSize) {
    return false
  }

  if (uploadUsage !== MediaUploadUsage.StorefrontHero.Value) {
    return true
  }

  const sourceType = media.photo_asset?.source_type ?? media.source_type
  if (
    media.upload_usage &&
    media.upload_usage !== MediaUploadUsage.StorefrontHero.Value &&
    sourceType !== 'stock'
  ) {
    return false
  }

  const width = Number(media.width)
  const height = Number(media.height)
  const restrictions = profile.savedMediaRestrictions

  if (
    restrictions &&
    Number.isFinite(width) &&
    Number.isFinite(height) &&
    width > 0 &&
    height > 0 &&
    (width > restrictions.maxWidth ||
      height > restrictions.maxHeight ||
      width * height > restrictions.maxPixels)
  ) {
    return false
  }

  return true
}

/**
 * The photo's latest file (else its original), tagged with the photo it came from. A typed photo always carries its
 * files. Historical display callers can pass one without them, or a plain file; it then comes back as given,
 * or as the `fallback` they pass. The finite view overload exposes that same existing input boundary.
 */
export function getAssetMedia(asset: PhotoAssetWithMedia): MediaFile
export function getAssetMedia(
  asset: MediaEditingAsset,
  fallback?: MediaEditingFile
): MediaEditingFile
export function getAssetMedia(
  asset: MediaEditingAsset,
  fallback: MediaEditingFile = asset
): MediaEditingFile {
  const media: MediaEditingFile = asset?.latest_media || asset?.original_media || fallback

  if (!media || media === fallback || !asset?.id) {
    return media
  }

  const photoAsset: MediaPhotoAsset = {
    id: asset.id,
    ...(asset.folder_id !== undefined ? { folder_id: asset.folder_id } : {}),
    ...(asset.original_media_id !== undefined
      ? { original_media_id: asset.original_media_id }
      : {}),
    ...(asset.latest_media_id !== undefined ? { latest_media_id: asset.latest_media_id } : {}),
    ...(asset.source_photo_asset_id !== undefined
      ? { source_photo_asset_id: asset.source_photo_asset_id }
      : {}),
    ...(asset.source_type !== undefined ? { source_type: asset.source_type } : {}),
  }

  return {
    ...media,
    photo_asset_id: media.photo_asset_id ?? asset.id,
    photo_asset: media.photo_asset ?? photoAsset,
  }
}

export function getMediaPhotoAssetId(
  media: Pick<MediaFile, 'photo_asset_id' | 'photo_asset'> | null | undefined
): number | null {
  const id = media?.photo_asset_id ?? media?.photo_asset?.id
  const numericId = Number(id)

  return Number.isFinite(numericId) && numericId > 0 ? numericId : null
}

export function getStockSourcePhotoAssetId(
  media: (Pick<MediaFile, 'photo_asset'> & PhotoAssetSource) | null | undefined
): number | null {
  const photoAsset = media?.photo_asset ?? media
  if (photoAsset?.source_type !== 'stock') {
    return null
  }

  const numericId = Number(photoAsset?.source_photo_asset_id)

  return Number.isFinite(numericId) && numericId > 0 ? numericId : null
}

export function selectFirstValidAssetMedia(
  assets?: readonly PhotoAssetWithMedia[],
  options?: { uploadUsage?: MediaUploadUsageValue }
): AssetSelectionCounts & { selectedMedia: MediaFile | null }
export function selectFirstValidAssetMedia(
  assets?: readonly MediaEditingAsset[],
  options?: { uploadUsage?: MediaUploadUsageValue }
): AssetSelectionCounts & { selectedMedia: MediaEditingFile | null }
export function selectFirstValidAssetMedia(
  assets: readonly MediaEditingAsset[] = [],
  { uploadUsage }: { uploadUsage?: MediaUploadUsageValue } = {}
): AssetSelectionCounts & { selectedMedia: MediaEditingFile | null } {
  let selectedMedia: MediaEditingFile | null = null
  let skippedCount = 0

  for (const asset of assets) {
    const media = getAssetMedia(asset)

    if (
      !media?.id ||
      !media?.url ||
      selectedMedia ||
      (uploadUsage && !isMediaCompatibleWithUploadUsage(media, uploadUsage))
    ) {
      skippedCount++
      continue
    }

    selectedMedia = media
  }

  return {
    selectedMedia,
    insertedCount: selectedMedia ? 1 : 0,
    skippedCount,
  }
}

export function canEditMedia(
  media: Pick<MediaFile, 'url' | 'mime_type' | 'name'> | null | undefined
): boolean {
  if (!media?.url) {
    return false
  }

  const mimeType = String(media.mime_type || '').toLowerCase()
  const name = String(media.name || media.url || '')

  if (UNSUPPORTED_EDIT_MIME_TYPES.has(mimeType)) {
    return false
  }

  return !UNSUPPORTED_EDIT_EXTENSIONS.test(name)
}

export function getEditedMediaName(
  media: Pick<MediaFile, 'name'> | null | undefined,
  outputType: string | null | undefined
): string {
  const baseName = String(media?.name || 'photo')
    .trim()
    .replace(IMAGE_EXTENSION_REGEX, '')
  const extension = MIME_EXTENSION_MAP[String(outputType || '').toLowerCase()] || 'png'

  return `${baseName || 'photo'}-edited.${extension}`
}

export function mergeEditedMedia<M extends MediaFile | null | undefined>(
  currentMedia: M,
  editedMedia: MediaFile | null | undefined
): M | MediaFile
export function mergeEditedMedia<M extends MediaEditingFile | null | undefined>(
  currentMedia: M,
  editedMedia: MediaEditingFile | null | undefined
): M | MediaEditingFile
export function mergeEditedMedia<M extends MediaEditingFile | null | undefined>(
  currentMedia: M,
  editedMedia: MediaEditingFile | null | undefined
): M | MediaEditingFile {
  if (!editedMedia?.id || !editedMedia?.url) {
    return currentMedia
  }

  return {
    ...currentMedia,
    ...editedMedia,
    title: currentMedia?.title ?? editedMedia.title,
    caption: currentMedia?.caption ?? editedMedia.caption,
  }
}

export function editedMediaDuplicatesExistingItem(
  mediaItems: (Pick<MediaFile, 'id'> | null | undefined)[] | null | undefined,
  editingMediaId: number | null | undefined,
  editedMedia: Pick<MediaFile, 'id'> | null | undefined
): boolean {
  if (!editedMedia?.id || !Array.isArray(mediaItems)) {
    return false
  }

  const editingIndex = mediaItems.findIndex((item) => Number(item?.id) === Number(editingMediaId))
  if (editingIndex < 0) {
    return false
  }

  const editedMediaId = Number(editedMedia.id)

  return mediaItems.some(
    (item, index) => index !== editingIndex && Number(item?.id) === editedMediaId
  )
}
